import { BundlePatientSummary } from "@/services/fhir/patientsummary/BundlePatientSummary";
import { GetBinary } from "@/services/fhir/patientsummary/Binary/GetBinary";
import { PatchEHRPoster } from "@/services/fhir/patientsummary/PatchEHRPoster";
import { auxiliarData } from "@/services/fhir/AuxiliarData";
import { endpoints } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useFhirData } from "@/utils/useFhirData";
import { httpClient, normalizeHttpError } from "src-next/shared/api/httpClient";

import type { PatientSupportingDocumentTypeId } from "../types";
import {
  PATIENT_SUPPORTING_DOCUMENT_CATEGORIES,
  getCategoryForTypeId,
} from "../types";

const MHD_FHIR_URL = `${endpoints.XDSB_domain}/mhd/fhir`;
const MHD_TIMEOUT_MS = 60000;

/** Tokens de filtro: só as 4 categories (não "DocumentReference" genérico). */
export const SUPPORTING_DOCUMENT_SUMMARY_ENTRY_FILTERS: PatientSupportingDocumentTypeId[] =
  [
    "anvisa_import_authorization",
    "medical_prescription",
    "personal_document",
    "proof_of_residence",
  ];

export interface PostSupportingDocumentParams {
  /** Identificador do paciente sem prefixo OID (ex.: CPF). */
  patientId: string;
  categoryValue: string;
  categoryDisplay: string;
  /** MIME real do arquivo (pdf, jpeg, png, etc.). */
  contentType: string;
  /** Base64 puro, sem prefixo `data:*;base64,`. */
  base64: string;
  /** Identificação opcional exibida ao paciente. */
  description?: string;
}

/** Identifier official (`use: official`) do documento a ser substituído. */
export interface PreviousDocumentIdentifier {
  system: string;
  value: string;
}

export interface ReplaceSupportingDocumentParams
  extends PostSupportingDocumentParams {
  previousIdentifier: PreviousDocumentIdentifier;
}

export interface PostSupportingDocumentResult {
  response: unknown;
  documentReferenceId?: string;
}

export interface ListSupportingDocumentsByPatientParams {
  patientId: string;
}

export interface GetSupportingDocumentByIdParams {
  documentReferenceId: string;
}

export interface CancelSupportingDocumentParams {
  documentReferenceId: string;
}

/** Formato consumido pela UI de documentos de suporte. */
export interface ListedSupportingDocument {
  typeId: PatientSupportingDocumentTypeId;
  documentReferenceId?: string;
  mimeType: string;
  /** Data URL para preview (Binary carregado sob demanda no list). */
  dataUrl: string;
  /** URL relativa do Binary no MHD (fallback se dataUrl falhar). */
  binaryUrl?: string;
  fileName: string;
  description?: string;
  notes: string;
  updatedAt: string;
  /** Data de envio do arquivo (DocumentReference.date). */
  sentAt?: string;
  /** Data da remoção lógica (meta.lastUpdated) — só em cancelados. */
  removedAt?: string;
  /** Identifier official do documento — necessário para substituição (relatesTo). */
  previousIdentifier?: PreviousDocumentIdentifier;
  /**
   * docStatus FHIR (ex.: final, cancelled).
   * Remoção lógica usa "cancelled"; ativos normalmente não trazem o campo.
   */
  docStatus?: string;
}

export interface ListSupportingDocumentsResult {
  active: ListedSupportingDocument[];
  cancelled: ListedSupportingDocument[];
}

function assertPostParams(params: PostSupportingDocumentParams): void {
  if (!params.patientId?.trim()) {
    throw new Error("patientId é obrigatório para enviar o documento.");
  }
  if (!params.base64?.trim()) {
    throw new Error("base64 é obrigatório para enviar o documento.");
  }
  if (!params.contentType?.trim()) {
    throw new Error("contentType é obrigatório para enviar o documento.");
  }
  if (!params.categoryValue?.trim() || !params.categoryDisplay?.trim()) {
    throw new Error("categoryValue e categoryDisplay são obrigatórios.");
  }
}

function pickDocumentReferences(
  searchsetBundle: any,
): fhir5.DocumentReference[] {
  const entries = searchsetBundle?.entry;
  if (!Array.isArray(entries)) return [];

  // Inclui cancelados (docStatus) para o histórico da UI separar ativos x removidos.
  return entries
    .filter(
      (entry: any) => entry?.resource?.resourceType === "DocumentReference",
    )
    .map((entry: any) => entry.resource as fhir5.DocumentReference);
}

function isCancelledDocStatus(docStatus: unknown): boolean {
  return String(docStatus ?? "").toLowerCase() === "cancelled";
}

function extensionFromMime(mimeType: string): string {
  if (mimeType.includes("pdf")) return "pdf";
  if (mimeType.includes("png")) return "png";
  if (mimeType.includes("jpeg") || mimeType.includes("jpg")) return "jpg";
  return "bin";
}

async function resolveBinaryDataUrl(
  binaryUrl: string | undefined,
  fallbackContentType: string,
): Promise<{ dataUrl: string; mimeType: string }> {
  if (!binaryUrl?.trim()) {
    return { dataUrl: "", mimeType: fallbackContentType };
  }

  try {
    const binary = await GetBinary({ urlBinary: binaryUrl });
    const mimeType =
      binary?.contentType?.trim() ||
      fallbackContentType ||
      "application/octet-stream";
    const base64 = binary?.data;
    if (!base64) {
      return { dataUrl: "", mimeType };
    }
    return {
      dataUrl: `data:${mimeType};base64,${base64}`,
      mimeType,
    };
  } catch {
    // Preview pode falhar sem derrubar a listagem dos metadados.
    return { dataUrl: "", mimeType: fallbackContentType };
  }
}

/**
 * POST MHD: Bundle transaction DocumentReference + Binary.
 * Usa o mesmo endpoint/contrato de `PostResultJson` (não usar fhirClients.admin).
 * `relatesToQuery` (interno): quando presente, o novo DocumentReference sai com
 * relatesTo "replaces" apontando para o documento anterior (substituição).
 */
async function sendSupportingDocument(
  params: PostSupportingDocumentParams,
  relatesToQuery?: string,
): Promise<PostSupportingDocumentResult> {
  assertPostParams(params);

  const payload = auxiliarData.DocumentReferencePatientJson(
    params.base64,
    params.patientId.trim(),
    params.categoryValue.trim(),
    params.categoryDisplay.trim(),
    params.contentType.trim(),
    relatesToQuery,
    params.description,
  );

  let documentReferenceId: string | undefined;
  try {
    const parsed = JSON.parse(payload) as {
      entry?: Array<{ resource?: { id?: string } }>;
    };
    documentReferenceId = parsed?.entry?.[0]?.resource?.id;
  } catch {
    documentReferenceId = undefined;
  }

  try {
    // Body em string JSON — paridade com PostResultJson (servidor MHD).
    const response = await httpClient.post(MHD_FHIR_URL, payload, {
      timeout: MHD_TIMEOUT_MS,
      headers: {
        Accept: "application/fhir+json",
        "Content-Type": "application/fhir+json",
      },
      validateStatus: (status) =>
        status === HTTPResponseStatus.Success ||
        status === HTTPResponseStatus.Created,
    });

    return { response: response.data, documentReferenceId };
  } catch (error) {
    const httpError = normalizeHttpError(error);
    throw new Error(
      httpError.status
        ? `Falha ao enviar documento (HTTP ${httpError.status}).`
        : "Falha ao enviar documento de suporte.",
    );
  }
}

/** Envio de documento novo (sem vínculo com anterior). */
async function postSupportingDocument(
  params: PostSupportingDocumentParams,
): Promise<PostSupportingDocumentResult> {
  return sendSupportingDocument(params);
}

/**
 * Monta a query de busca do doc anterior: "identifier=<system>%7C<value>".
 * Pipe encodado como %7C, igual ao legado (CertificateActions/auxReplaces).
 */
function buildRelatesToQuery(
  previousIdentifier: PreviousDocumentIdentifier,
): string {
  const system = previousIdentifier?.system?.trim();
  const value = previousIdentifier?.value?.trim();
  if (!system || !value) {
    throw new Error(
      "Identifier do documento anterior é obrigatório para substituição.",
    );
  }
  return `identifier=${system}%7C${value}`;
}

/**
 * Substituição: novo POST com relatesTo "replaces" apontando para o doc anterior.
 * O servidor versiona — não há DELETE do documento antigo.
 */
async function replaceSupportingDocument(
  params: ReplaceSupportingDocumentParams,
): Promise<PostSupportingDocumentResult> {
  const relatesToQuery = buildRelatesToQuery(params.previousIdentifier);
  return sendSupportingDocument(params, relatesToQuery);
}

/**
 * Atalho tipado: monta category a partir do typeId do catálogo.
 * `previousIdentifier` (opcional) transforma o envio em substituição.
 */
async function postSupportingDocumentByType(params: {
  patientId: string;
  typeId: PatientSupportingDocumentTypeId;
  contentType: string;
  base64: string;
  description?: string;
  previousIdentifier?: PreviousDocumentIdentifier;
}): Promise<PostSupportingDocumentResult> {
  const category = getCategoryForTypeId(params.typeId);
  const baseParams: PostSupportingDocumentParams = {
    patientId: params.patientId,
    categoryValue: category.value,
    categoryDisplay: category.display,
    contentType: params.contentType,
    base64: params.base64,
    description: params.description,
  };

  if (params.previousIdentifier) {
    return replaceSupportingDocument({
      ...baseParams,
      previousIdentifier: params.previousIdentifier,
    });
  }
  return postSupportingDocument(baseParams);
}

/**
 * Lista documentos de suporte via Patient Summary MHD.
 * Filtra só pelas 4 categories — não traz Observation/Condition/outros DocumentReference.
 * Retorna ativos e cancelados separados (remoção lógica = docStatus cancelled).
 */
async function listSupportingDocumentsByPatient(
  params: ListSupportingDocumentsByPatientParams,
): Promise<ListSupportingDocumentsResult> {
  const patientId = params.patientId?.trim();
  if (!patientId) {
    throw new Error("patientId é obrigatório para listar documentos.");
  }

  const summary = await BundlePatientSummary({
    identifier: patientId,
    // Tokens = category na URL (url.includes), não resourceType genérico.
    entries: SUPPORTING_DOCUMENT_SUMMARY_ENTRY_FILTERS,
    purposeofuse: "Atendimento",
  });

  if (
    summary &&
    typeof summary === "object" &&
    "status" in summary &&
    (summary as { status: number }).status === HTTPResponseStatus.Forbidden
  ) {
    throw new Error("Acesso negado ao resumo do paciente (403).");
  }

  const active: ListedSupportingDocument[] = [];
  const cancelled: ListedSupportingDocument[] = [];

  for (const typeId of SUPPORTING_DOCUMENT_SUMMARY_ENTRY_FILTERS) {
    const searchsets = useFhirData.extractPatientSummaryResource(
      summary,
      "DocumentReference",
      typeId,
    );
    const documentReferences =
      searchsets?.flatMap(pickDocumentReferences) ?? [];

    for (const docRef of documentReferences) {
      const attachment = docRef.content?.[0]?.attachment;
      const binaryUrl = attachment?.url;
      const attachmentContentType =
        attachment?.contentType?.trim() || "application/octet-stream";
      const { dataUrl, mimeType } = await resolveBinaryDataUrl(
        binaryUrl,
        attachmentContentType,
      );

      const category = PATIENT_SUPPORTING_DOCUMENT_CATEGORIES[typeId];
      const fileName =
        attachment?.title?.trim() ||
        `${category.display}.${extensionFromMime(mimeType)}`;

      // Identifier official (urn:uuid) — usado no relatesTo da substituição.
      const officialIdentifier =
        docRef.identifier?.find((id) => id.use === "official") ??
        docRef.identifier?.[1];
      const previousIdentifier =
        officialIdentifier?.system && officialIdentifier?.value
          ? {
              system: officialIdentifier.system,
              value: officialIdentifier.value,
            }
          : undefined;

      const docStatus =
        typeof docRef.docStatus === "string" ? docRef.docStatus : undefined;

      const sentAt =
        docRef.date ||
        attachment?.creation ||
        new Date().toISOString();
      // Remoção lógica atualiza meta.lastUpdated no PATCH de docStatus.
      const removedAt = isCancelledDocStatus(docStatus)
        ? docRef.meta?.lastUpdated || undefined
        : undefined;

      const item: ListedSupportingDocument = {
        typeId,
        documentReferenceId: docRef.id,
        mimeType,
        dataUrl,
        binaryUrl,
        fileName,
        description: docRef.description?.trim() || undefined,
        notes: "",
        // Lista ativa continua usando updatedAt como data de envio.
        updatedAt: sentAt,
        sentAt,
        removedAt,
        previousIdentifier,
        docStatus,
      };

      if (isCancelledDocStatus(docStatus)) {
        cancelled.push(item);
      } else {
        active.push(item);
      }
    }
  }

  return { active, cancelled };
}

/**
 * Remoção lógica: preserva o recurso para auditoria e impede sua exibição.
 * `add` funciona tanto para criar docStatus quanto para atualizar a propriedade.
 */
async function cancelSupportingDocument(
  params: CancelSupportingDocumentParams,
): Promise<void> {
  const documentReferenceId = params.documentReferenceId?.trim();
  if (!documentReferenceId) {
    throw new Error("Documento não identificado para remoção.");
  }

  await PatchEHRPoster({
    resourceType: "DocumentReference",
    resourceId: documentReferenceId,
    operation: "add",
    path: "/docStatus",
    value: "cancelled",
  });
}

/**
 * TODO: ler um DocumentReference por id.
 * Preparado — não implementar GET unitário nesta entrega.
 */
async function getSupportingDocumentById(
  _params: GetSupportingDocumentByIdParams,
): Promise<never> {
  throw new Error("getSupportingDocumentById ainda não foi implementado.");
}

export const patientSupportingDocumentsService = {
  postSupportingDocument,
  postSupportingDocumentByType,
  replaceSupportingDocument,
  listSupportingDocumentsByPatient,
  cancelSupportingDocument,
  getSupportingDocumentById,
};
